import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        isOpen &&
        !target.closest('.mobile-menu') &&
        !target.closest('.hamburger-button')
      ) {
        setIsOpen(false);
      }
      if (
        isLanguageOpen &&
        !target.closest('.language-menu') &&
        !target.closest('.language-button')
      ) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen, isLanguageOpen]);

  const navigationItems = [
    { id: 'services', label: t('navigation.services'), href: '#services' },
    { id: 'about', label: t('navigation.about'), href: '#about' },
    { id: 'contact', label: t('navigation.contact'), href: '#contact' },
  ];

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-black/90 backdrop-blur-sm' : 'bg-transparent'
    }`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="relative grid grid-cols-3 items-center">
          {/* Centered Logo - visible on mobile and desktop */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <img src="/FR-outline.svg" alt="La Frencherie" className="w-8 h-8" />
          </div>
          {/* Logo */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              type="button"
              onClick={handleRefresh}
              className="text-white font-light tracking-widest text-xs sm:text-sm lg:text-lg bg-transparent p-0 m-0 border-0 cursor-pointer"
              aria-label="Reload page"
            >
              LA FRENCHERIE
            </button>
          </div>

          {/* Desktop Navigation with Language Selector - moved to right column to avoid overlapping the centered logo */}
          <nav className="hidden lg:flex justify-end col-start-3 items-center space-x-8 xl:space-x-12">
            {navigationItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                className="text-white font-light tracking-widest text-xs xl:text-sm hover:text-gray-300 transition-colors duration-300 whitespace-nowrap"
              >
                {item.label}
              </a>
            ))}
            
            {/* Language Selector - aligned with navigation */}
            <button
              type="button"
              className="text-white p-2 language-button"
              onClick={() => setIsLanguageOpen(!isLanguageOpen)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                />
              </svg>
            </button>
          </nav>

          {/* Mobile/Tablet: Language Selector and Hamburger Menu Button */}
          <div className="flex justify-end col-start-3 items-center space-x-2 lg:hidden">
            {/* Language Selector - visible on mobile/tablet */}
            <button
              type="button"
              className="text-white p-2 language-button"
              onClick={() => setIsLanguageOpen(!isLanguageOpen)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                />
              </svg>
            </button>
            
            {/* Hamburger Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white p-2 hamburger-button"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu - Dropdown */}
        {isOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-700 mobile-menu bg-black/90 backdrop-blur-sm">
            <div className="flex flex-col space-y-4 pt-4 items-center">
              {navigationItems.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  className="text-gray-300 hover:text-white transition-colors duration-300 font-light tracking-wide text-sm"
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Language Menu */}
        {isLanguageOpen && (
          <div className="mt-4 pb-4 border-t border-gray-700 language-menu bg-black/90 backdrop-blur-sm">
            <div className="flex flex-col space-y-4 pt-4 items-center">
              {['en', 'fr', 'es', 'de', 'it', 'pt', 'ru', 'ja', 'zh', 'ar', 'hi', 'ko'].map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={`text-gray-300 hover:text-white transition-colors duration-300 font-light tracking-wide text-sm ${
                    i18n.language === lang ? 'text-white' : ''
                  }`}
                  onClick={() => {
                    const currentScroll = window.scrollY;
                    i18n.changeLanguage(lang);
                    setIsLanguageOpen(false);
                    // Safe scroll restoration that respects hook boundaries
                    setTimeout(() => {
                      const scrollHeight = document.documentElement.scrollHeight;
                      const clientHeight = window.innerHeight;
                      const maxScroll = scrollHeight - clientHeight;
                      const safeScroll = Math.min(currentScroll, maxScroll);
                      if (safeScroll >= 0) {
                        window.scrollTo(0, safeScroll);
                      }
                    }, 0);
                  }}
                >
                  {t(`languageSelector.languages.${lang}`)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 